import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import { useAuth } from '../contexts/AuthContext';
import { validateEmail } from '../utils/validation';

/**
 * 네이버 N 로고 SVG 아이콘
 */
const NaverIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M13.56 10.7L6.15 3H3v14h3.44V9.3L13.85 17H17V3h-3.44v7.7z"
      fill="#03C75A"
    />
  </svg>
);

/**
 * 카카오 말풍선 SVG 아이콘
 */
const KakaoIcon = () => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
    <path
      d="M10 3C5.58 3 2 5.79 2 9.21c0 2.17 1.45 4.08 3.63 5.17l-.93 3.42c-.08.3.26.54.52.37l4.07-2.68c.23.02.47.03.71.03 4.42 0 8-2.79 8-6.21S14.42 3 10 3z"
      fill="#3C1E1E"
    />
  </svg>
);

/**
 * 구글 4색 G 로고 SVG 아이콘
 */
const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
      fill="#4285F4"
    />
    <path
      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
      fill="#34A853"
    />
    <path
      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
      fill="#FBBC05"
    />
    <path
      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
      fill="#EA4335"
    />
  </svg>
);

function Login() {
  const { handleLogin } = useAuth();
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const data = await authService.login(credentials.email, credentials.password);
      handleLogin(data.user, data.token);
    } catch (error) {
      let errorMessage = '로그인 중 오류가 발생했습니다.';

      if (error.code === 'RATE_LIMITED') {
        errorMessage = '너무 많은 로그인 시도가 있었습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.code === 'UNAUTHORIZED') {
        errorMessage = '이메일 또는 비밀번호가 올바르지 않습니다.';
      } else if (error.code === 'NETWORK_ERROR') {
        errorMessage = '네트워크 오류가 발생했습니다. 인터넷 연결을 확인해주세요.';
      } else if (error.code === 'SERVER_ERROR') {
        errorMessage = '서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.';
      } else if (error.userMessage) {
        errorMessage = error.userMessage;
      }

      setError(errorMessage);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });

    // 실시간 이메일 유효성 검사
    if (name === 'email') {
      const emailError = validateEmail(value);
      setFieldErrors({
        ...fieldErrors,
        email: emailError
      });
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-header">
          <h1 className="auth-title">MoneyMind</h1>
          <p className="auth-subtitle">로그인하여 시작하세요</p>
        </div>

        {error && (
          <div className="auth-error">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="login-email">
              이메일 주소
            </label>
            <input
              id="login-email"
              type="email"
              name="email"
              value={credentials.email}
              onChange={handleChange}
              className={fieldErrors.email ? 'has-error' : ''}
              placeholder="이메일을 입력하세요"
              required
            />
            {fieldErrors.email && (
              <div className="form-error">
                {fieldErrors.email}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="login-password">
              비밀번호
            </label>
            <input
              id="login-password"
              type="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              placeholder="비밀번호를 입력하세요"
              required
            />
          </div>

          <button
            type="submit"
            className="btn-primary btn-full-width"
          >
            로그인
          </button>
        </form>

        {/* 소셜 로그인 구분선 */}
        <div className="auth-divider">
          <span>또는</span>
        </div>

        {/* 소셜 로그인 버튼들 */}
        <div className="social-buttons">
          <button
            onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || ''}/api/v1/auth/naver`}
            className="social-btn social-btn--naver"
          >
            <NaverIcon />
            네이버로 시작하기
          </button>

          <button
            onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || ''}/api/v1/auth/kakao`}
            className="social-btn social-btn--kakao"
          >
            <KakaoIcon />
            카카오로 시작하기
          </button>

          <button
            onClick={() => window.location.href = `${process.env.REACT_APP_API_URL || ''}/api/v1/auth/google`}
            className="social-btn social-btn--google"
          >
            <GoogleIcon />
            구글로 시작하기
          </button>
        </div>

        <div className="auth-footer">
          <div className="auth-footer-row">
            <span className="auth-subtitle">계정이 없으신가요? </span>
            <button
              onClick={() => navigate('/register')}
              className="auth-link"
            >
              회원가입
            </button>
          </div>
          <div>
            <button
              onClick={() => navigate('/forgot-password')}
              className="auth-link auth-link--danger"
            >
              비밀번호를 잊으셨나요?
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
